import { Vector3 } from 'three'
import type { Pilot } from '../physics/Pilot'
import type { PadInfo } from '../render/world'

export type FlightState = 'ready' | 'flying' | 'landed'

const LANDING_SPEED = 1.5
const LANDING_TILT = 25
const LANDING_DWELL = 0.6
/** One impact must not be counted on every frame it stays in contact. */
const CRASH_COOLDOWN = 1.5
const BEST_TIME_KEY = 'rocketmanxr.bestTime'

/**
 * The First Flight challenge (DESIGN.md §11) — launch pad, landing pad 50 m
 * away, "launch, fly over, land without crashing". One timer and one crash
 * counter is the entire Phase 1 game loop, and it is enough to measure whether
 * skill is building session over session.
 */
export class FirstFlight {
  state: FlightState = 'ready'
  /** Seconds since leaving the launch pad. */
  elapsed = 0
  crashes = 0
  bestTime: number | null = null
  /** Set for one frame when an attempt completes, for the HUD to celebrate. */
  justCompleted = false
  /** Set for one frame when a crash is counted — the app respawns on this. */
  justCrashed = false
  lastMessage = ''

  private dwell = 0
  private crashCooldown = 0

  constructor(
    private readonly launch: PadInfo,
    private readonly landing: PadInfo,
  ) {
    const saved = Number(globalThis.localStorage?.getItem(BEST_TIME_KEY))
    this.bestTime = Number.isFinite(saved) && saved > 0 ? saved : null
  }

  get distanceToTarget(): number {
    return this.horizontalDistance(_last, this.landing.position)
  }

  update(pilot: Pilot, dt: number): void {
    this.justCompleted = false
    this.justCrashed = false
    this.crashCooldown = Math.max(0, this.crashCooldown - dt)
    _last.copy(pilot.body.position)

    if (pilot.lastContact === 'crash' && this.crashCooldown === 0) {
      this.crashes++
      this.crashCooldown = CRASH_COOLDOWN
      this.justCrashed = true
      this.lastMessage = `crash at ${pilot.lastImpactSpeed.toFixed(1)} m/s`
      this.reset()
      return
    }

    // Vertical tolerance matters now that pads sit on rooftops: being 60 m
    // directly above the landing pad is not being on it.
    const overLanding =
      this.horizontalDistance(pilot.body.position, this.landing.position) < this.landing.radius &&
      Math.abs(pilot.body.position.y - this.landing.position.y) < 3
    const clearedLaunch =
      pilot.body.position.y - this.launch.position.y > 1.5 ||
      this.horizontalDistance(pilot.body.position, this.launch.position) > this.launch.radius

    switch (this.state) {
      case 'ready':
        if (clearedLaunch) {
          this.state = 'flying'
          this.elapsed = 0
          this.lastMessage = ''
        }
        break

      case 'flying': {
        this.elapsed += dt
        const settled =
          pilot.grounded &&
          overLanding &&
          pilot.body.velocity.length() < LANDING_SPEED &&
          pilot.tiltDegrees() < LANDING_TILT
        this.dwell = settled ? this.dwell + dt : 0
        if (this.dwell >= LANDING_DWELL) this.complete()
        break
      }

      case 'landed':
        // Taking off again starts a fresh attempt.
        if (!pilot.grounded) {
          this.state = 'ready'
          this.dwell = 0
        }
        break
    }
  }

  private complete(): void {
    this.state = 'landed'
    this.dwell = 0
    this.justCompleted = true
    if (this.bestTime === null || this.elapsed < this.bestTime) {
      this.bestTime = this.elapsed
      try {
        globalThis.localStorage?.setItem(BEST_TIME_KEY, String(this.elapsed))
      } catch {
        /* storage unavailable */
      }
      this.lastMessage = `landed — new best ${this.elapsed.toFixed(1)}s`
    } else {
      this.lastMessage = `landed in ${this.elapsed.toFixed(1)}s`
    }
  }

  /** Back to the launch pad without touching the crash counter. */
  reset(): void {
    this.state = 'ready'
    this.elapsed = 0
    this.dwell = 0
  }

  resetSession(): void {
    this.reset()
    this.crashes = 0
    this.crashCooldown = 0
    this.lastMessage = ''
  }

  private horizontalDistance(a: Vector3, b: Vector3): number {
    const dx = a.x - b.x
    const dz = a.z - b.z
    return Math.hypot(dx, dz)
  }
}

const _last = new Vector3()
